import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, X } from 'lucide-react';
import { STRINGS } from '@/strings/strings';

/**
 * Top bar of the Now-Playing full-screen. Three zones:
 *   - `X` on the left — dismisses the takeover. Uses `navigate(-1)` when
 *     there is browser history (normal case: arrived via MiniPlayer tap),
 *     else falls back to `/` (deep-link / fresh-tab case).
 *   - Static label "Wird momentan abgespielt" — the Now-Playing title.
 *   - Menu area on the right. When `menuSlot` is provided, renders that
 *     node (used by NowPlayingPage to mount `<NowPlayingMenu />` — the
 *     Popover trigger is the 3-dots icon button, content portals out).
 *     When `menuSlot` is undefined, renders an inert 3-dots button so
 *     the layout still has an anchor for visual balance (Commit 3
 *     placeholder before the menu was wired).
 *
 * `navigate(-1)` is the correct behaviour for modal-style takeover pages:
 * close returns to whatever initiated the takeover (Story page, Library,
 * Start, etc.), not to a fixed home. The fallback to `/` only triggers
 * when `window.history.length <= 1` — which in practice only happens on
 * fresh-tab deep links.
 */
interface NowPlayingTopNavProps {
  menuSlot?: ReactNode;
}

export function NowPlayingTopNav({ menuSlot }: NowPlayingTopNavProps) {
  const navigate = useNavigate();
  const copy = STRINGS.shell.nowPlaying;

  const handleClose = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="flex h-14 items-center justify-between gap-4 px-4 md:h-16">
      <button
        type="button"
        onClick={handleClose}
        aria-label={copy.ariaLabel.close}
        className="flex h-10 w-10 items-center justify-center text-dream-white opacity-75 transition-opacity hover:opacity-100"
      >
        <X className="h-6 w-6" strokeWidth={2} />
      </button>

      <span className="truncate text-center text-sm font-medium leading-none text-dream-white md:text-base">
        {copy.topNavTitle}
      </span>

      {menuSlot ?? (
        <button
          type="button"
          disabled
          aria-label={copy.ariaLabel.menu}
          className="flex h-10 w-10 items-center justify-center text-dream-white opacity-25 disabled:cursor-default"
        >
          <MoreVertical className="h-6 w-6" strokeWidth={2} />
        </button>
      )}
    </div>
  );
}
